import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Loader2, CheckCircle2, Truck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { WB_DISTRICTS, VEHICLE_TYPES, SERVICE_TYPES } from "@/lib/constants";
import { ORDER_IDS } from "@/constants/testIds/site";

const EMPTY = {
  name: "", phone: "", whatsapp: "", email: "",
  vehicle_number: "", vehicle_type: "", vehicle_rc: "",
  district: "", address: "", pincode: "",
  service_type: "", notes: "",
};

export default function OrderForm({ defaultService }) {
  const [form, setForm] = useState({ ...EMPTY, service_type: defaultService || "" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const navigate = useNavigate();

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v?.target ? v.target.value : v }));

  const validate = useMemo(() => () => {
    const e = {};
    if (!form.name || form.name.trim().length < 2) e.name = "Name is required";
    if (!/^\+?\d{7,15}$/.test((form.phone || "").replace(/\s+/g, ""))) e.phone = "Enter a valid phone number";
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.vehicle_number || form.vehicle_number.trim().length < 4) e.vehicle_number = "Vehicle number is required";
    if (!form.vehicle_type) e.vehicle_type = "Select vehicle type";
    if (!form.district) e.district = "Select district";
    if (!form.address || form.address.trim().length < 5) e.address = "Address is required";
    if (!/^\d{6}$/.test(form.pincode || "")) e.pincode = "6-digit pincode";
    if (!form.service_type) e.service_type = "Select a service";
    return e;
  }, [form]);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) {
      toast.error("Please fix the highlighted fields");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        whatsapp: form.whatsapp || null,
        email: form.email || null,
        vehicle_rc: form.vehicle_rc || null,
        notes: form.notes || null,
      };
      const { data } = await api.post("/orders", payload);
      setSubmitted(data);
      setForm({ ...EMPTY, service_type: defaultService || "" });
      toast.success("Order submitted! Our team will contact you shortly.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    if (defaultService) setForm((s) => ({ ...s, service_type: defaultService }));
  }, [defaultService]);

  if (submitted) {
    return (
      <div data-testid={ORDER_IDS.success} className="bg-white rounded-3xl border border-[#E2E8F0] p-10 text-center shadow-sm">
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-600 grid place-items-center mx-auto">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h3 className="mt-4 font-display text-2xl font-bold text-[#111827]">Order received</h3>
        <p className="mt-2 text-[#6B7280] max-w-md mx-auto">
          Thanks, <span className="font-semibold text-[#111827]">{submitted.name}</span>! Your reference ID is
          <span className="ml-1 font-mono text-sm px-2 py-0.5 bg-[#F8FAFC] border border-[#E2E8F0] rounded">{submitted.id.slice(0, 8)}</span>.
          Our team will reach out via phone/WhatsApp on <span className="font-semibold">{submitted.phone}</span>.
        </p>
        <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
          <Button onClick={() => setSubmitted(null)} className="bg-[#0057B8] hover:bg-[#003F87] text-white rounded-full btn-brand" data-testid="order-new-btn">
            Place another order
          </Button>
          <Button onClick={() => navigate("/")} variant="outline" className="border-[#0057B8] text-[#0057B8] rounded-full btn-brand">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const fieldCls = "bg-white border-[#E2E8F0] focus-visible:ring-[#0057B8] focus-visible:ring-2 focus-visible:ring-offset-2";
  const errCls = "text-xs text-red-600 mt-1";

  return (
    <form onSubmit={onSubmit} className="bg-white rounded-3xl border border-[#E2E8F0] p-6 sm:p-8 shadow-sm" data-testid={ORDER_IDS.form}>
      <div className="flex items-start gap-3 mb-6">
        <div className="w-11 h-11 rounded-xl bg-[#F59E0B]/15 text-[#B45309] grid place-items-center"><Truck className="w-5 h-5" /></div>
        <div>
          <h3 className="font-display text-xl sm:text-2xl font-bold text-[#111827]">FASTag Order & Enquiry</h3>
          <p className="text-sm text-[#6B7280]">Fill in your details — we deliver across all 23 districts of West Bengal.</p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor={ORDER_IDS.name} className="text-sm font-semibold text-[#111827]">Full Name <span className="text-red-500">*</span></Label>
          <Input id={ORDER_IDS.name} data-testid={ORDER_IDS.name} value={form.name} onChange={set("name")} placeholder="e.g. Rahul Das" className={fieldCls} />
          {errors.name && <div className={errCls}>{errors.name}</div>}
        </div>
        <div>
          <Label htmlFor={ORDER_IDS.phone} className="text-sm font-semibold text-[#111827]">Phone <span className="text-red-500">*</span></Label>
          <Input id={ORDER_IDS.phone} data-testid={ORDER_IDS.phone} value={form.phone} onChange={set("phone")} placeholder="10-digit mobile" className={fieldCls} inputMode="tel" />
          {errors.phone && <div className={errCls}>{errors.phone}</div>}
        </div>
        <div>
          <Label htmlFor={ORDER_IDS.whatsapp} className="text-sm font-semibold text-[#111827]">WhatsApp (optional)</Label>
          <Input id={ORDER_IDS.whatsapp} data-testid={ORDER_IDS.whatsapp} value={form.whatsapp} onChange={set("whatsapp")} placeholder="Same as phone if applicable" className={fieldCls} />
        </div>
        <div>
          <Label htmlFor={ORDER_IDS.email} className="text-sm font-semibold text-[#111827]">Email (optional)</Label>
          <Input id={ORDER_IDS.email} data-testid={ORDER_IDS.email} value={form.email} onChange={set("email")} placeholder="you@example.com" className={fieldCls} type="email" />
          {errors.email && <div className={errCls}>{errors.email}</div>}
        </div>

        <div>
          <Label htmlFor={ORDER_IDS.vehicleNumber} className="text-sm font-semibold text-[#111827]">Vehicle Number <span className="text-red-500">*</span></Label>
          <Input id={ORDER_IDS.vehicleNumber} data-testid={ORDER_IDS.vehicleNumber} value={form.vehicle_number} onChange={(e) => set("vehicle_number")(e.target.value.toUpperCase())} placeholder="e.g. WB19A1234" className={fieldCls} />
          {errors.vehicle_number && <div className={errCls}>{errors.vehicle_number}</div>}
        </div>
        <div>
          <Label className="text-sm font-semibold text-[#111827]">Vehicle Type <span className="text-red-500">*</span></Label>
          <Select value={form.vehicle_type} onValueChange={set("vehicle_type")}>
            <SelectTrigger data-testid={ORDER_IDS.vehicleType} className={fieldCls}><SelectValue placeholder="Select vehicle type" /></SelectTrigger>
            <SelectContent>
              {VEHICLE_TYPES.map(v => <SelectItem key={v} value={v}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          {errors.vehicle_type && <div className={errCls}>{errors.vehicle_type}</div>}
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor={ORDER_IDS.vehicleRc} className="text-sm font-semibold text-[#111827]">RC / Chassis (optional)</Label>
          <Input id={ORDER_IDS.vehicleRc} data-testid={ORDER_IDS.vehicleRc} value={form.vehicle_rc} onChange={set("vehicle_rc")} placeholder="RC number or Chassis (helps speed up processing)" className={fieldCls} />
        </div>

        <div>
          <Label className="text-sm font-semibold text-[#111827]">Delivery District (WB) <span className="text-red-500">*</span></Label>
          <Select value={form.district} onValueChange={set("district")}>
            <SelectTrigger data-testid={ORDER_IDS.district} className={fieldCls}><SelectValue placeholder="Select district" /></SelectTrigger>
            <SelectContent className="max-h-72">
              {WB_DISTRICTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
          {errors.district && <div className={errCls}>{errors.district}</div>}
        </div>
        <div>
          <Label htmlFor={ORDER_IDS.pincode} className="text-sm font-semibold text-[#111827]">Pincode <span className="text-red-500">*</span></Label>
          <Input id={ORDER_IDS.pincode} data-testid={ORDER_IDS.pincode} value={form.pincode} onChange={set("pincode")} placeholder="6-digit" className={fieldCls} inputMode="numeric" maxLength={6} />
          {errors.pincode && <div className={errCls}>{errors.pincode}</div>}
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor={ORDER_IDS.address} className="text-sm font-semibold text-[#111827]">Full Address <span className="text-red-500">*</span></Label>
          <Textarea id={ORDER_IDS.address} data-testid={ORDER_IDS.address} value={form.address} onChange={set("address")} rows={3} placeholder="Flat/House, Street, Area, Landmark" className={fieldCls} />
          {errors.address && <div className={errCls}>{errors.address}</div>}
        </div>

        <div className="sm:col-span-2">
          <Label className="text-sm font-semibold text-[#111827]">Service Type <span className="text-red-500">*</span></Label>
          <Select value={form.service_type} onValueChange={set("service_type")}>
            <SelectTrigger data-testid={ORDER_IDS.serviceType} className={fieldCls}><SelectValue placeholder="Which service do you need?" /></SelectTrigger>
            <SelectContent>
              {SERVICE_TYPES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          {errors.service_type && <div className={errCls}>{errors.service_type}</div>}
        </div>

        <div className="sm:col-span-2">
          <Label htmlFor={ORDER_IDS.notes} className="text-sm font-semibold text-[#111827]">Notes (optional)</Label>
          <Textarea id={ORDER_IDS.notes} data-testid={ORDER_IDS.notes} value={form.notes} onChange={set("notes")} rows={3} placeholder="Anything else you'd like us to know" className={fieldCls} />
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="text-xs text-[#6B7280]">
          By submitting, you agree to be contacted by our team on the provided phone/WhatsApp.
        </div>
        <Button
          type="submit"
          disabled={submitting}
          data-testid={ORDER_IDS.submit}
          className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full btn-brand h-11 px-8 shadow active:scale-95"
        >
          {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</> : "Submit Order"}
        </Button>
      </div>
    </form>
  );
}
